import { BTNS, SECTION_TYPE } from 'src/utils/constants';
import { ts } from 'src/utils/translate';
import {
  initButtonDescriptors,
  SECTION_BUTTON_FLOOR_PREFIX,
  SECTION_BUTTON_FLOOR_ROOMS_PREFIX,
  SECTION_BUTTON_ROOM_PREFIX,
  SectionButtonDescriptor
} from 'src/pages/OfferPage/DocuSectionButtons/docuSectionButtonDefs';
import { ProjectQuoteUserProperties } from 'src/model/api/project.model';
import { GetProjectTreeProjectChildNode } from 'src/model/api/get-project-tree-project-childnode.model';
import { ProjectQuoteFloorVisibility } from 'src/api/project-quote-ui-slice';
import { getRoomsOfFloor } from 'src/utils/projectTreeAdapterForOfferPage';
import { GetQuoteResponse } from 'src/model/api/quote/get-quote-response.model';
import { mapDeviceSymbols } from 'src/pages/OfferPage/CableLabel/CableLabel';
import { PinoutData } from 'src/pages/OfferPage/pinouts/PinoutConverter';
import { NHCPinoutDevices } from 'src/model/api/nhc-pinout-devices';
import { FeatureFlagResponse } from 'src/model/api/feature-flag.model';
import { GetFloorRoomsDevice } from 'src/model/api/v2/data-contracts';

export function mapQuoteUserPropertiesToButtonDescriptors(
    projectTreeNodes: GetProjectTreeProjectChildNode[],
    quoteUserProperties: ProjectQuoteUserProperties,
    floorVisibilities: ProjectQuoteFloorVisibility[],
    tableData: any[],
    isPro: boolean,
    isBelgianUser: boolean,
    quote: GetQuoteResponse,
    devices: GetFloorRoomsDevice[],
    pinoutData: boolean | undefined | PinoutData,
    nhcPinoutData: NHCPinoutDevices,
    cableListData: any,
    groupedDevicesData: any,
    featureFlag: FeatureFlagResponse,
    svgData: any,
    lightingCalcs: any
): SectionButtonDescriptor[] {
  const descriptors = initButtonDescriptors();

  insertRoomButtons(descriptors, projectTreeNodes, floorVisibilities);

  mapState(descriptors, quoteUserProperties, floorVisibilities);

  displayLoxoneOrNHCDINDevices(descriptors, tableData);
  disableProOnlyButtons(descriptors, isPro);
  disableButtonsWithMissingData(descriptors, tableData, quote, devices, projectTreeNodes,
      pinoutData, nhcPinoutData, cableListData, groupedDevicesData);
  disableMapButtonsWithoutSvgData(descriptors, svgData, lightingCalcs);
  disableNHCForNotBelgianUser(descriptors, isBelgianUser);
  hideTestFeatureButtons(descriptors, featureFlag);
  disableCostRelatedButtonsWhenCostsAreHidden(descriptors);

  hideButtonsOfClosedFloors(descriptors, floorVisibilities);
  return descriptors;
}

function insertRoomButtons(
    descriptors: SectionButtonDescriptor[],
    projectTreeNodes: GetProjectTreeProjectChildNode[],
    floorVisibilities: ProjectQuoteFloorVisibility[],
) {
  const roomButtonDescriptors = calcRoomButtonDescriptors(projectTreeNodes, floorVisibilities);
  descriptors.splice(
      descriptors.findIndex((desc) => desc.buttonId === BTNS.FUNCTION_DESCRIPTION) + 1,
      0,
      ...roomButtonDescriptors
  );
}

function mapState(
    descriptors: SectionButtonDescriptor[],
    quoteUserProperties: ProjectQuoteUserProperties,
    floorVisibilities: ProjectQuoteFloorVisibility[],
) {
  descriptors.forEach((desc) => {
    if (desc.propertyKey === 'include_client_type') {
      desc.state = quoteUserProperties.include_client_type.valueOf();
    } else if (desc.type === 'normal') {
      // @ts-ignore: it could be string or number
      desc.state = quoteUserProperties[desc.propertyKey];
    } else if (desc.type === 'floor') {
      const floorVisibility = floorVisibilities.find((floorVisibility) => floorVisibility.floorId === desc.floorId)
      desc.state = floorVisibility!!.floorPlanVisible ? 1 : 0;
    } else if (desc.type === 'floor_rooms') {
      const floorVisibility = floorVisibilities.find((floorVisibility) => floorVisibility.floorId === desc.floorId)
      desc.state = floorVisibility!!.roomFloorPlansVisible ? 1 : 0;
    }
  })
}

function disableProOnlyButtons(
    descriptors: SectionButtonDescriptor[],
    isPro: boolean
) {
  if (isPro) {
    return;
  }
  descriptors.forEach(desc => {
    if (desc.buttonId === BTNS.T_ARTICLENUMBERS) {
      desc.enabled = false;
    }
  })
}

function disableButtonsWithMissingData(
    descriptors: SectionButtonDescriptor[],
    tableData: any,
    quote: GetQuoteResponse,
    devices: GetFloorRoomsDevice[],
    projectTreeNodes: GetProjectTreeProjectChildNode[],
    pinoutData: boolean | undefined | PinoutData,
    nhcPinoutData: NHCPinoutDevices,
    cableListData: any,
    groupedDevicesData: any
) {
  if (tableData["guessed"] != null) {
    return
  }
  [
    {entitytype: "convfunctiontable", btnVar: BTNS.T_CAB_CONV},
    {entitytype: "nhcdintable", btnVar: BTNS.T_NHC_DIN_DEVICES},
    {entitytype: "racktable", btnVar: BTNS.T_RACK},
    {entitytype: "projectcablestable", btnVar: BTNS.T_CABLES},
    {entitytype: "functiontable", btnVar: BTNS.T_LOXONE_DIN_DEVICES},
  ].forEach((elem) => {
    const tbl = tableData.find(
        (table: any) => table.entitytype === elem.entitytype
    );
    if (!tbl || (tbl && Array.isArray(tbl.rows) && tbl.rows.length === 0)) {
      disableButton(descriptors, elem.btnVar);
    }
  });

  const tableWithBackBox = tableData.find(
      (table: any) =>
          table.entitytype === "roomtable" &&
          table.rows.find((row: any) => row.is_back_box)
  );

  if (!tableWithBackBox) {
    disableButton(descriptors, BTNS.T_BACK_BOXES);
  }

  if (quote["project-base"] &&
      !(
          quote["project-base"].general_info &&
          quote["project-base"].general_info.trim() !== "<p><br></p>"
      )
  ) {
    disableButton(descriptors, BTNS.GENERAL);
  }


  let deviceSymbolIds = mapDeviceSymbols(devices, projectTreeNodes);
  if (deviceSymbolIds.length === 0) {
    disableButton(descriptors, BTNS.CABLE_LABELS);
  }

  if (
      pinoutData === false &&
      cableListData === false &&
      nhcPinoutData == null &&
      groupedDevicesData == null
  ) {
    disableButton(descriptors, BTNS.PINOUT)
  }

}

function disableNHCForNotBelgianUser(
    descriptors: SectionButtonDescriptor[],
    isBelgianUser: boolean
) {
  if (!isBelgianUser) {
    disableButton(descriptors, BTNS.T_NHC_DIN_DEVICES);
  }
}

function hideTestFeatureButtons(
    descriptors: SectionButtonDescriptor[],
    featureFlag: FeatureFlagResponse) {
  if (!featureFlag.test_features_enabled) {
    hideButton(descriptors, BTNS.MAP_LIGHTING);
  }
}

function disableMapButtonsWithoutSvgData(
    descriptors: SectionButtonDescriptor[],
    svgData: any,
    lightingCalcs: any) {
  if (!svgData || !Array.isArray(svgData.plannerSVGs)) {
    return;
  }
  const plannerSVGs = svgData.plannerSVGs;
  if (!plannerSVGs.length) {
    disableButton(descriptors, BTNS.MAP_SYMBOLS);
    disableButton(descriptors, BTNS.MAP_NOTES);
  }
  if (!plannerSVGs.some((svg: any) => svg.isWifi)) {
    disableButton(descriptors, BTNS.MAP_WIFI);
  }
  if (!plannerSVGs.some((svg: any) => svg.isWiring)) {
    disableButton(descriptors, BTNS.MAP_WIRING);
  }
  if (!plannerSVGs.some((svg: any) => svg.isCamera)) {
    disableButton(descriptors, BTNS.MAP_CAMERA);
  }
  if (lightingCalcs && Object.values(lightingCalcs).every((entry: any) => entry.status === 'dl_error')) {
    disableButton(descriptors, BTNS.MAP_LIGHTING);
  }
}

function disableCostRelatedButtonsWhenCostsAreHidden(descriptors: SectionButtonDescriptor[]) {
  const currencyVisibilityButton =
      (descriptors.find((descriptor) => descriptor.buttonId === BTNS.CURRENCY_VISIBILITY));

  if (currencyVisibilityButton?.state!! === 0) {
    disableButton(descriptors, BTNS.LABOUR_COST);
    disableButton(descriptors, BTNS.TOTAL_PRICE);
  }
}

function displayLoxoneOrNHCDINDevices(
    descriptors: SectionButtonDescriptor[],
    tableData: any,
) {
  const loxoneTableExists = tableData.find((tbl: any) =>
              tbl.entitytype === SECTION_TYPE.FUNCTION_TABLE && tbl.rows.length)
  const nhcTableExists = tableData.find((tbl: any) =>
      tbl.entitytype === SECTION_TYPE.NHC_DIN_TABLE && tbl.rows.length)
  const loxoneDesc = descriptors.find((desc) => desc.buttonId === BTNS.T_LOXONE_DIN_DEVICES)
  const nhcDesc = descriptors.find((desc) => desc.buttonId === BTNS.T_NHC_DIN_DEVICES)
  if (loxoneTableExists) {
    loxoneDesc!!.customizable = true
  }
  if (nhcTableExists) {
    nhcDesc!!.customizable = true
  }

}

function calcRoomButtonDescriptors(
    projectTreeNodes: GetProjectTreeProjectChildNode[],
    floorVisibilities: ProjectQuoteFloorVisibility[]
): SectionButtonDescriptor[] {
  let buttonDescriptors: SectionButtonDescriptor[] = []
  for (let floorVisibility of floorVisibilities) {
    // The floor may not be in the tree yet (e.g. right after cabinet creation, before
    // the project tree refetches); skip it until the data is in sync.
    const floorNode = projectTreeNodes.find((node) => node.id === `floor_${floorVisibility.floorId}`);
    if (!floorNode) {
      continue;
    }
    let floorName = floorNode.text;
    let rooms = getRoomsOfFloor(floorVisibility.floorId, projectTreeNodes);

    let floorButtonDesc = {
      buttonId: SECTION_BUTTON_FLOOR_PREFIX + floorVisibility.floorId,
      floorId: floorVisibility.floorId,
      propertyKey: '',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'floor',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/floor.svg",
      label: floorName,
      contentCssSelector: `.floor-header-container[data-floorid="${floorVisibility.floorId}"]`
    } as SectionButtonDescriptor;
    buttonDescriptors.push(floorButtonDesc);

    let floorRoomsButtonDesc = {
      buttonId: SECTION_BUTTON_FLOOR_ROOMS_PREFIX + floorVisibility.floorId,
      floorId: floorVisibility.floorId,
      propertyKey: '',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'floor_rooms',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/rooms.svg",
      label: ts("rooms", "Rooms"),
      contentCssSelector: `.floor-header-container[data-floorid="${floorVisibility.floorId}"]`
    } as SectionButtonDescriptor;
    buttonDescriptors.push(floorRoomsButtonDesc);

    rooms.forEach((room) => {
      buttonDescriptors.push({
        buttonId: SECTION_BUTTON_ROOM_PREFIX + room.id.replace('room_', ''),
        floorId: floorVisibility.floorId,
        roomId: Number(room.id.replace('room_', '')),
        propertyKey: '',
        enabled: true,
        visible: true,
        customizable: true,
        toggleable: false,
        type: 'room',
        state: 1,
        section: 'sections',
        icon: "/images/offer_icons/rooms.svg",
        label: `${floorName} - ${room.text}`,
        contentCssSelector: `.room-header-container[data-roomid="${room.id.replace('room_', '')}"]`
      })
    })
  }

  return buttonDescriptors;
}

function hideButtonsOfClosedFloors(
    descriptors: SectionButtonDescriptor[],
    floorVisibilities: ProjectQuoteFloorVisibility[]) {
  descriptors.forEach(desc => {
    if (desc.type === 'floor_rooms' || desc.type === 'room') {
      const visibility = floorVisibilities.find((floorVisibility) => floorVisibility.floorId === desc.floorId)
      desc.customizable = visibility ? visibility.dropDownOpened!! : false;
    }
  });

}
export function disableButton(
    descriptors: SectionButtonDescriptor[],
    buttonId: string
) {
  const desc = descriptors.find((desc) => desc.buttonId === buttonId)
  if (desc) {
    desc.enabled = false;
  }
}

function hideButton(
    descriptors: SectionButtonDescriptor[],
    buttonId: string
) {
  const desc = descriptors.find((desc) => desc.buttonId === buttonId)
  if (desc) {
    desc.customizable = false;
  }
}

export const getHighlightedBtn = (btns: any) => {
  if (!btns.length) {
    return false;
  }

  const windowScrollTop = /* window.pageYOffset + */ 140;

  const isAtBottom =
      window.innerHeight + window.scrollY >=
      Math.round(document.body.offsetHeight) - 1;

  let idx = 0;

  if (isAtBottom) {
    return btns[btns.length - 1];
  }

  const btnsWithElems = btns
  .map((btn: any) => {
    // TODO: resolvethis
    // const elem = document.querySelector(getSelectorForBtn(btn));
    // if (!elem) {
    //   return false;
    // }
    // const currTop = elem.getBoundingClientRect().top;
    // const dist = windowScrollTop - currTop;
    // return {btn, dist};
  })
  .filter((obj: any) => !!obj);

  if (!btnsWithElems.length) {
    return false;
  }

  btnsWithElems.sort((a: any, b: any) => (a.dist > b.dist ? -1 : 1));

  while (idx < btnsWithElems.length && btnsWithElems[idx].dist > 0) {
    idx++;
  }

  if (idx > 0) {
    idx--;
  }

  return btnsWithElems[idx].btn;
};